import { useEffect } from 'react';

/**
 * Detects when a newer build has been deployed and reloads the tab to pick it up.
 *
 * A single-page app keeps running whatever bundle it loaded at page open; a new
 * deploy does NOT reach an already-open tab until the page reloads. This hook
 * closes that gap: it polls /version.json (which the build stamps with a unique
 * buildId) and, when the deployed id differs from the one baked into this bundle
 * (__BUILD_ID__), reloads.
 *
 * Guards:
 *  - Only runs in production builds (no reload churn during `vite dev`).
 *  - Never reloads while the user is typing in a field, so in-progress entry is
 *    not clobbered — it retries on the next tick once focus has moved.
 *  - Checks on an interval AND whenever the tab regains focus/visibility, so a
 *    tab left open for days updates the moment the user returns to it.
 */
export function useVersionCheck(intervalMs = 60_000): void {
  useEffect(() => {
    if (!import.meta.env.PROD) return;

    let stopped = false;

    const isEditing = (): boolean => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (el as HTMLElement).isContentEditable
      );
    };

    const check = async (): Promise<void> => {
      if (stopped || isEditing()) return;
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { buildId?: string };
        if (data.buildId && data.buildId !== __BUILD_ID__ && !stopped && !isEditing()) {
          window.location.reload();
        }
      } catch {
        // Offline or transient network error — ignore and try again next tick.
      }
    };

    const id = window.setInterval(check, intervalMs);
    const onWake = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    void check();

    return () => {
      stopped = true;
      window.clearInterval(id);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [intervalMs]);
}
